/**
 * Clock Panel 动作管线(web 版,刻意比 mobile 薄):
 *   pending(防连点)→ 调 API → toast → **无论成败 refreshAll()**。
 * 零乐观更新——失败即重拉服务端状态,两端(mobile+web)一致性由此收敛。
 * actionTime 用 serverClock(Date 响应头校准),后端 ACTION_TIME_TOO_EARLY 兜底。
 */
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { attendanceApi, tasksApi } from "@/api/api";
import { taskActions } from "@/services/taskActionsService";
import {
  AnotherTaskInProgressError,
  AttendanceActionTimeTooEarlyError,
  AttendanceAlreadyClockedInError,
  AttendanceInBreakCannotClockOutError,
  OpenWorkSessionExistsError,
  parseApiError,
} from "@/services/errors";
import { serverNowIso } from "@/utils/serverClock";
import type { SnowflakeId } from "@/types/snowflake";
import type { BreakTypeId } from "./derive";

/** 模块级纯函数,不进 useCallback 依赖数组(模块级标识符不需要)。 */
const nowIso = () => serverNowIso();

export type ClockActionKind =
  | "clockIn"
  | "clockOut"
  | "startBreak"
  | "endBreak"
  | "startWorkType"
  | "endWorkType";

/** axios 错误 → typed Error(有业务错误码时走 parseApiError)。 */
function toTypedError(e: unknown): Error {
  const resp = (e as { response?: { status?: number; data?: any } })?.response;
  if (resp?.data && (resp.data.error || resp.data.error_code)) {
    return parseApiError(resp.status ?? 0, resp.data);
  }
  return e instanceof Error ? e : new Error(String(e));
}

/** 错误 → 用户可见英文文案(spec §4 冲突处理表)。 */
export function clockErrorMessage(err: Error): string {
  if (err instanceof AttendanceAlreadyClockedInError)
    return "Already clocked in (possibly from another device).";
  if (err instanceof AttendanceInBreakCannotClockOutError)
    return "End your break before clocking out.";
  if (err instanceof OpenWorkSessionExistsError)
    return "You have a running task. Finish or pause it first.";
  if (err instanceof AnotherTaskInProgressError)
    return "Another task is in progress.";
  if (err instanceof AttendanceActionTimeTooEarlyError)
    return "Action time conflicts with a newer record. Status refreshed — try again.";
  return err.message || "Action failed.";
}

/** 任意异常 → 用户可见英文文案(typed error 或 axios 业务错误码)。 */
export function toClockErrorMessage(e: unknown): string {
  return clockErrorMessage(toTypedError(e));
}

interface UseClockActionsArgs {
  userId: SnowflakeId | null;
  /** 动作结束后(无论成败)重拉服务端状态(attendance + open sessions)。 */
  refreshAll: () => Promise<unknown>;
}

export function useClockActions({ userId, refreshAll }: UseClockActionsArgs) {
  const [pending, setPending] = useState<ClockActionKind | null>(null);
  // 防连点守卫走 ref:run 的身份不随 pending 变化重建,避免下游 callback 链整体刷新
  const pendingRef = useRef<ClockActionKind | null>(null);

  const run = useCallback(
    async (kind: ClockActionKind, fn: () => Promise<unknown>, successMsg: string) => {
      if (!userId || pendingRef.current) return;
      pendingRef.current = kind;
      setPending(kind);
      try {
        await fn();
        toast.success(successMsg);
      } catch (e) {
        // 诊断:dump 原始 response shape,后端问题直接拿日志去找后端
        console.error(
          `[clock-panel] ${kind} failed:`,
          (e as { response?: { data?: unknown } })?.response?.data ?? e,
        );
        toast.error(clockErrorMessage(toTypedError(e)));
      } finally {
        try {
          await refreshAll();
        } finally {
          pendingRef.current = null;
          setPending(null);
        }
      }
    },
    [userId, refreshAll],
  );

  const clockInAction = useCallback(
    () => run("clockIn", () => attendanceApi.clockIn(String(userId), { actionTime: nowIso() }), "Clocked in"),
    [run, userId],
  );
  const clockOutAction = useCallback(
    () => run("clockOut", () => attendanceApi.clockOut(String(userId), { actionTime: nowIso() }), "Clocked out"),
    [run, userId],
  );
  const startBreakAction = useCallback(
    (breakType: BreakTypeId) =>
      run(
        "startBreak",
        () => attendanceApi.startBreak(String(userId), { actionTime: nowIso(), notes: breakType }),
        "Break started",
      ),
    [run, userId],
  );
  const endBreakAction = useCallback(
    () => run("endBreak", () => attendanceApi.endBreak(String(userId), { actionTime: nowIso() }), "Break ended"),
    [run, userId],
  );

  /** 两步契约(对齐 mobile startSpecialTask):先建 special task,再 start。 */
  const startWorkTypeAction = useCallback(
    (subtypeCode: string, label: string, notes: string) =>
      run(
        "startWorkType",
        async () => {
          const created = (await tasksApi.addTask({
            taskTypeCode: "special",
            title: label,
            taskSubtypeCode: subtypeCode,
            priority: "low",
            description: notes || undefined,
            plannedStartAt: nowIso(),
            assigneeId: String(userId),
          })) as { id?: string } | undefined;
          const createdId = created?.id;
          if (!createdId) {
            console.error(
              "[clock-panel] POST /pms/tasks response missing id, shape:",
              created,
            );
            throw new Error("Task creation returned no id.");
          }
          await taskActions.start(String(createdId), notes ? { notes } : {});
        },
        "Activity started",
      ),
    [run, userId],
  );

  /** special finish 必须不带 reason_code(带了后端 422)。 */
  const endWorkTypeAction = useCallback(
    (taskId: string, notes?: string) =>
      run(
        "endWorkType",
        () => taskActions.finish(taskId, notes ? { notes } : {}),
        "Activity ended",
      ),
    [run],
  );

  /**
   * 跨天收口编排：按后端守卫要求的顺序串行。无 toast——失败上抛由
   * CrossDayPrompt 内联展示并可重试(对齐 Bug Ownership:失败不静默)。
   *   1. finish open special(否则 _guard_no_open_work_session 挡补签退)
   *   2. endBreak(backdated)——仅 in_break
   *   3. clockOut(backdated)——后端按 action_time 解析到昨天那行闭合
   */
  const resolveCrossDay = useCallback(
    async (args: {
      openSpecialTaskId: string | null;
      endBreakIso: string | null;
      clockOutIso: string;
    }) => {
      // 防御性守卫:userId 为 null 时 CrossDayPrompt 不应渲染,此路不可达;
      // 万一触发,用已映射的通用文案,避免内部短语漏到 UI 错误框。
      if (!userId) throw new Error("Action failed.");
      if (args.openSpecialTaskId) {
        // special finish 不带 reason_code(后端 422)
        await taskActions.finish(args.openSpecialTaskId, {});
      }
      if (args.endBreakIso) {
        await attendanceApi.endBreak(String(userId), { actionTime: args.endBreakIso });
      }
      await attendanceApi.clockOut(String(userId), { actionTime: args.clockOutIso });
    },
    [userId],
  );

  return {
    pending,
    clockIn: clockInAction,
    clockOut: clockOutAction,
    startBreak: startBreakAction,
    endBreak: endBreakAction,
    startWorkType: startWorkTypeAction,
    endWorkType: endWorkTypeAction,
    resolveCrossDay,
  };
}
